import { useState } from "react";
import { useParams } from "react-router-dom";
import { getFileTypeIcon } from "../../../../../../../../../utils/functions/file-icon-extractor";
import Button from "../../../../../../../../../components/Button/Button";
import ChevronDownIcon from "../../../../../../../../../assets/svg/chevron-down-icon";
import DownloadIcon from "../../../../../../../../../assets/svg/download-icon";
import AttachmentPreviewModal from "../../../../../../../../Communications/components/CommunicationDetailsPanel/components/AttachmentPreviewModal";
import { Attachment } from "../../../../../../../../Communications/utils/types";
import GalleryIcon from "../../../../../../../../../assets/svg/gallery-icon";
import { TicketAttachment } from "../../../../../utils/types";
import {
  useGetTicketAttachmentsQuery,
  useDownloadTicketAttachmentsMutation,
} from "../../../../../../../../../redux/services/helpdeskApi";
import { customToast } from "../../../../../../../../../utils/custom-toast/custom-toast";
import AttachmentShimmer from "./AttachmentShimmer";

const TicketAttachments = () => {
  const { ticketId = "" } = useParams();
  const { data, isLoading } = useGetTicketAttachmentsQuery(ticketId, {
    skip: !ticketId,
  });
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<string | false>(
    false
  );
  const [showAll, setShowAll] = useState(false);
  const [downloadTicketAttachments, { isLoading: isDownloading }] =
    useDownloadTicketAttachmentsMutation();

  const attachments: TicketAttachment[] = data?.attachments ?? [];

  const handleDownloadAll = async () => {
    if (!ticketId) return;
    try {
      const blob = await downloadTicketAttachments({ ticketId }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ticket-${ticketId}-attachments.zip`;
      document.body.appendChild(link);
      link?.click();
      link.remove();

      window.URL.revokeObjectURL(url);
      customToast("Attachments downloaded successfully", "success");
    } catch (error) {
      customToast("Failed to download attachments", "error");
      console.error(error);
    }
  };

  const displayedAttachments = showAll
    ? attachments
    : attachments?.slice(0, 10);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between h-[50px] px-3 border-b border-border-gray dark:border-border-gray-dk shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
            Attachments
          </h3>
          {!isLoading && (
            <span className="text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk px-1.5 py-0.5 border border-gray-100 dark:border-gray-100-dk rounded-sm">
              {attachments?.length}
            </span>
          )}
        </div>
        {!isLoading && attachments?.length > 0 && (
          <Button
            innerText="Download All"
            buttonContainerClass="text-verba-blue dark:text-verba-blue-dk w-fit hover:underline"
            innerTextClass="text-xs font-medium"
            Icon={DownloadIcon}
            iconLeft={true}
            iconClass="w-3.5 h-3.5 text-verba-blue dark:text-verba-blue-dk"
            onClick={handleDownloadAll}
            isLoading={isDownloading}
          />
        )}
      </div>

      {isLoading ? (
        <AttachmentShimmer />
      ) : attachments?.length > 0 ? (
        <div className="flex flex-col gap-4 px-3 py-4">
          <div className="flex items-center gap-2 flex-wrap min-h-[60px]">
            {displayedAttachments?.map((attachment) => (
              <div
                key={attachment?.s3_key}
                className="flex items-center gap-2 w-[60px] h-[60px] rounded-lg bg-border-gray dark:bg-border-gray-dk border border-border-gray dark:border-border-gray-dk justify-center cursor-pointer"
                onClick={() => setIsPreviewModalOpen(attachment?.s3_key)}
              >
                <img
                  src={
                    attachment?.content_type?.startsWith("image/")
                      ? attachment?.s3_url
                      : getFileTypeIcon(attachment?.file_name)
                  }
                  alt={attachment?.file_name}
                  className={`object-cover rounded-lg ${
                    attachment?.content_type?.startsWith("image/")
                      ? "w-full h-full"
                      : "w-6 h-6"
                  }`}
                />
              </div>
            ))}
            <AttachmentPreviewModal
              open={Boolean(isPreviewModalOpen)}
              onOpenChange={(open) =>
                setIsPreviewModalOpen(open ? isPreviewModalOpen : false)
              }
              attachments={(attachments || []) as Attachment[]}
              initialIndex={attachments?.findIndex(
                (attachment) => attachment?.s3_key === isPreviewModalOpen
              )}
            />
          </div>
          {attachments?.length > 10 && (
            <Button
              innerText={showAll ? "Show less" : "View more"}
              buttonContainerClass="text-verba-blue dark:text-verba-blue-dk w-fit"
              innerTextClass="text-md font-medium"
              Icon={ChevronDownIcon}
              iconClass={`w-[14px] h-[14px] text-verba-blue dark:text-verba-blue-dk font-normal ${
                showAll ? "rotate-180" : ""
              }`}
              onClick={() => setShowAll(!showAll)}
            />
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center h-full mb-10">
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="border border-gray-25 dark:border-gray-25-dk rounded-[21px] p-3">
              <div className="border border-gray-50 dark:border-gray-50-dk rounded-[21px] p-3">
                <div className="border border-gray-100 dark:border-gray-100-dk rounded-[21px] p-3">
                  <GalleryIcon className="w-[40px] h-[40px] text-gray-150 dark:text-gray-150-dk" />
                </div>
              </div>
            </div>
            <span className="text-md text-data dark:text-data-dk font-medium">
              No attachments added yet
            </span>
            <span className="text-md text-gray-placeholder dark:text-gray-placeholder-dk text-center">
              Nothing shared so far. Attachments <br /> will show up here.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketAttachments;
